import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { default as Airplane } from "@/assets/illustrations/airplane.svg"
import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import {
  GatedLookupSessionsError,
  GatedRegisterSessionError,
} from "@/utilities/expectedErrors"
import { sessionDetailsQueryOptions } from "@/utilities/hooks/queries/express"
import {
  GET_CONFIRMATION_DETAILS_BASE_KEY,
  SESSION_BASE_KEY,
} from "@/utilities/hooks/queries/keys"
import {
  confirmSetupIntentQueryOptions,
  setupIntentQueryOptions,
} from "@/utilities/hooks/queries/payment"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/complete"
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { sessionID } = Route.useParams()
  const navigate = Route.useNavigate()
  const { express } = Route.useSearch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { data: sessionRsp } = useSuspenseQuery(
    sessionDetailsQueryOptions({ sessionID })
  )
  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card>
        <Airplane />
        <Heading className="center" level={1} as={1}>
          {t("registrationComplete.header")}
        </Heading>
        <Text
          className="center"
          level={1}
          style={{ marginTop: "var(--space-3)" }}
        >
          {t("registrationComplete.copy")}
        </Text>
      </Card>
      <Text style={{ marginTop: "var(--space-5)" }} className="center">
        {t("registrationComplete.reregister")}{" "}
        <Button
          hue="neutral"
          presentation="link"
          isPending={isPending}
          onPress={() => mutate()}
        >
          {t("common.clickHere")}
        </Button>
      </Text>
    </div>
  )

  async function mutationFn() {
    if (express) {
      const lookUpRsp = await api.lookupSessions.POST({
        plate: sessionRsp.session.cameraPlate,
        method: "plate",
        bypassSessionsLookup: true,
      })

      if ("failReason" in lookUpRsp) {
        throw new GatedLookupSessionsError(lookUpRsp.failReason)
      }
    }

    const rsp = await api.registerSession.POST({
      sessionID,
      phone: null,
      isInLane: false,
      isReregistering: true,
    })

    if ("failReason" in rsp) {
      throw new GatedRegisterSessionError(rsp.failReason)
    }

    return rsp
  }

  async function onSuccess() {
    queryClient.removeQueries({
      queryKey: setupIntentQueryOptions().queryKey,
    })
    queryClient.removeQueries({
      queryKey: confirmSetupIntentQueryOptions({ sessionID }).queryKey,
    })
    queryClient.removeQueries({
      queryKey: [
        SESSION_BASE_KEY,
        sessionID,
        GET_CONFIRMATION_DETAILS_BASE_KEY,
      ],
    })
    navigate({
      to: "/qr/$qrLookupID/gated/register/$sessionID/cart",
      search: { reregister: true },
      replace: true,
    })
  }
}
